/**
 * ImpersonationBanner — faixa laranja fixa no topo de TODAS as telas enquanto
 * um admin está atuando como usuário de um tenant.
 *
 * O estado vem de `GET /auth/me` (`impersonation`), porque o cookie do token é
 * httpOnly e o navegador não consegue ler o claim `impersonated_by`.
 */
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "../ui/sonner";
import api, { formatApiError } from "../../utils/api";
import { Theater, LogOut, Loader2, LifeBuoy } from "lucide-react";

const ALTURA = 36;

export default function ImpersonationBanner() {
  const { user } = useAuth();
  const [saindo, setSaindo] = useState(false);
  const imp = user?.impersonation || null;
  const suporte = imp?.mode === "support";
  const [restante, setRestante] = useState("");

  useEffect(() => {
    if (!imp?.session_expires_at) return undefined;
    const tick = () => {
      const ms = new Date(imp.session_expires_at).getTime() - Date.now();
      if (ms <= 0) return setRestante("expirando");
      const h = Math.floor(ms / 3600000);
      const m = Math.floor((ms % 3600000) / 60000);
      setRestante(h > 0 ? `${h}h${String(m).padStart(2, "0")}` : `${m} min`);
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [imp?.session_expires_at]);

  useEffect(() => {
    if (!imp) return undefined;
    // Empurra o app para baixo — vale para qualquer layout (inclusive telas
    // sem DashboardLayout, como o Painel Logístico).
    const anterior = document.body.style.paddingTop;
    document.body.style.paddingTop = `${ALTURA}px`;
    // Telas em `fixed inset-0` (Wallboard) ignoram o padding do body; elas
    // leem esta variável para descer o próprio topo.
    document.documentElement.style.setProperty("--imp-bar", `${ALTURA}px`);
    return () => {
      document.body.style.paddingTop = anterior;
      document.documentElement.style.removeProperty("--imp-bar");
    };
  }, [imp]);

  const voltarComoAdmin = useCallback(async () => {
    setSaindo(true);
    try {
      await api.post("/admin/impersonate/end");
      window.location.href = "/admin/clients";
    } catch (err) {
      setSaindo(false);
      toast.error("Falha ao voltar como admin", {
        description: formatApiError(err.response?.data?.detail),
      });
    }
  }, []);

  if (!imp) return null;

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-[200] text-white flex items-center justify-between gap-3 px-4 shadow-md ${suporte ? "bg-sky-600" : "bg-orange-500"}`}
      style={{ height: ALTURA }}
      data-testid="impersonation-banner"
    >
      <div className="flex items-center gap-2 min-w-0">
        {suporte ? <LifeBuoy size={15} className="shrink-0" /> : <Theater size={15} className="shrink-0" />}
        <span className="text-[11px] font-bold uppercase tracking-wider shrink-0">
          {suporte ? "Acesso de suporte" : "Modo impersonation"}
        </span>
        {suporte ? (
          <span className="text-xs truncate text-sky-50" data-testid="support-banner-text">
            Você está em <strong className="text-white">{user?.tenant_name || "tenant"}</strong> como{" "}
            <strong className="text-white">Suporte Autem</strong> (administrador)
            {restante ? ` — encerra em ${restante}` : ""}. Sessão de{" "}
            <strong className="text-white">{imp.by_email}</strong>, tudo auditado.
          </span>
        ) : (
          <span className="text-xs truncate text-orange-50">
            Você está atuando como <strong className="text-white">{user?.name || user?.email}</strong>
            {user?.email && user?.name ? ` (${user.email})` : ""}
            {user?.tenant_name ? ` · ${user.tenant_name}` : ""} — sessão iniciada por{" "}
            <strong className="text-white">{imp.by_email}</strong>. Todas as ações são auditadas.
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={voltarComoAdmin}
        disabled={saindo}
        className={`shrink-0 inline-flex items-center gap-1.5 h-7 px-3 rounded-md bg-card hover:bg-muted/50 text-xs font-bold transition-colors disabled:opacity-60 ${suporte ? "text-sky-700" : "text-orange-700"}`}
        data-testid="impersonation-end-btn"
      >
        {saindo ? <Loader2 size={12} className="animate-spin" /> : <LogOut size={12} />}
        {suporte ? "Sair do acesso" : "Voltar como admin"}
      </button>
    </div>
  );
}
